import AxiosInstance from "./Axios";
import Cookies from "universal-cookie";

// --------------------------------------------------------
// ENDPOINTS
// --------------------------------------------------------
const API_BASE = "/admin/client";

const API_TRANSACTION = "/admin/transaction";

// --------------------------------------------------------
// HELPERS
// --------------------------------------------------------
function normalizeStatusToBackend(raw) {
  const s = String(raw ?? "").trim().toUpperCase();
  if (s === "ENABLED") return "ACTIVE";
  if (s === "DISABLED") return "INACTIVE";
  return s;
}

function logValidationError(message) {
  console.error(message);
  return null;
}

function ensure3LetterCurrency(cur) {
  const c = String(cur ?? "").trim().toUpperCase();
  if (!c) return logValidationError("Falta el campo obligatorio: currency");
  if (c.length !== 3) return logValidationError("currency invalido. Usa EUR, USD, etc.");
  return c;
}

function normalizeTxType(raw) {
  const t = String(raw ?? "").trim();
  const tl = t.toLowerCase();
  if (tl === "withdraw") return "Withdraw";
  if (tl === "deposit") return "Deposit";
  return t; // si el backend usa otro enum, lo deja pasar
}

// --------------------------------------------------------
// GET BROKERS/IBs
// --------------------------------------------------------
export async function BrokersIBs() {
  try {
    const cookies = new Cookies();
    const token = cookies.get("token");

    return await AxiosInstance.get(API_BASE, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
    });
  } catch (error) {
    console.error("error in request:", error);
    return null;
  }
}

// --------------------------------------------------------
// BULK UPDATE STATUS
// --------------------------------------------------------
export async function BrokersIBsUpdateStatus(ids, statusValue) {
  const url = `${API_BASE}/status`;

  if (!Array.isArray(ids) || ids.length === 0) {
    return logValidationError("Debes enviar al menos un id en 'ids'.");
  }
  if (!statusValue) {
    return logValidationError("El parametro 'statusValue' es obligatorio.");
  }

  const body = { ids, status: statusValue };

  try {
    const cookies = new Cookies();
    const token = cookies.get("token");

    const response = await AxiosInstance.put(url, body, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
    });

    return response;
  } catch (error) {
    console.error("error in request:", error);
    return null;
  }
}

// --------------------------------------------------------
// UPDATE BROKER / IB
// --------------------------------------------------------
export async function UpdateBroker(broker) {
  const id = broker?.id ?? broker?.userId ?? broker?.user_id;
  if (!id) return logValidationError("Falta el campo obligatorio: id");

  const name = String(broker?.name ?? "").trim();
  const email = String(broker?.email ?? "").trim();
  const report_email = String(
    broker?.report_email ?? broker?.reportEmail ?? broker?.reportEmailAddress ?? ""
  ).trim();

  const user_type = String(broker?.user_type ?? broker?.userType ?? broker?.type ?? "")
    .trim()
    .toUpperCase();

  const rawStatus = broker?.status_backend ?? broker?.status ?? broker?.account_status ?? "";
  const status_backend = normalizeStatusToBackend(rawStatus);

  const account_status =
    String(broker?.account_status ?? "").trim().toUpperCase() ||
    (status_backend === "ACTIVE" ? "ACTIVE" : status_backend === "INACTIVE" ? "INACTIVE" : "");

  const currency = ensure3LetterCurrency(broker?.currency);
  const password = String(broker?.password ?? "").trim(); // opcional

  if (!currency) return null;
  if (!name) return logValidationError("Falta el campo obligatorio: name");
  if (!email) return logValidationError("Falta el campo obligatorio: email");
  if (!user_type) return logValidationError("Falta el campo obligatorio: user_type");
  if (!account_status) return logValidationError("Falta el campo obligatorio: account_status");

  const body = {
    id,
    name,
    email,
    user_type,
    status: status_backend,
    account_status,
    currency,
    ...(report_email ? { report_email } : {}),
    ...(password ? { password } : {}),
    country: String(broker?.country ?? "").trim(),
    city: String(broker?.city ?? "").trim(),
    address: String(broker?.address ?? "").trim(),
    phone: String(broker?.phone ?? "").trim(),
  };

  try {
    const cookies = new Cookies();
    const token = cookies.get("token");

    const res = await AxiosInstance.put(API_BASE, body, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
    });

    return res;
  } catch (error) {
    return error?.response ?? null;
  }
}

// --------------------------------------------------------
// DELETE BROKER / IB (SUPER ADMIN)
// --------------------------------------------------------
export async function DeleteBroker(id) {
  if (!id) return logValidationError("Falta el campo obligatorio: id");

  try {
    const cookies = new Cookies();
    const token = cookies.get("token");

    return await AxiosInstance.delete(API_BASE, {
      params: { id },
      headers: {
        'Authorization': `Bearer ${token}`
      },
    });
  } catch (error) {
    console.error("error in request:", error);
    const apiMessage =
      error?.response?.data?.message ??
      error?.message;

    throw new Error(apiMessage || "Error deleting user. Please try again.");
  }
}

// --------------------------------------------------------
// CREATE BROKER / IB
// --------------------------------------------------------
export async function CreateBroker(broker) {
  if (!broker || typeof broker !== "object") {
    return logValidationError("Debes enviar un objeto 'broker'.");
  }

  const name = String(broker.name ?? "").trim();
  const email = String(broker.email ?? "").trim();
  const password = broker.password;
  const report_email = String(broker.report_email ?? broker.reportEmail ?? "").trim();
  const user_type = String(broker.user_type ?? broker.userType ?? "").trim().toUpperCase();
  const status_backend = String(broker.status_backend ?? broker.status ?? "").trim();
  const account_status =
    String(broker.account_status ?? "").trim().toUpperCase() ||
    (String(broker.status || "").toLowerCase() === "enabled" ? "ACTIVE" : "") ||
    (String(broker.status || "").toLowerCase() === "disabled" ? "INACTIVE" : "");

  const currency = ensure3LetterCurrency(broker.currency);
  const country = String(broker.country ?? "").trim();
  const city = String(broker.city ?? "").trim();
  const address = String(broker.address ?? "").trim();
  const phone = String(broker.phone ?? "").trim();

  const required = {
    name,
    email,
    password,
    user_type,
    status: status_backend,
    account_status,
    currency,
  };

  const missing = Object.entries(required)
    .filter(([_, v]) => !String(v ?? "").trim())
    .map(([k]) => k);

  if (!currency) return null;
  if (missing.length) return logValidationError(`Faltan campos obligatorios: ${missing.join(", ")}`);
  if (!["IB", "BROKER"].includes(user_type)) {
    return logValidationError("user_type invalido. Usa 'IB' o 'BROKER'.");
  }
  if (!["ACTIVE", "INACTIVE"].includes(account_status)) {
    return logValidationError("account_status invalido. Usa 'ACTIVE' o 'INACTIVE'.");
  }

  const body = {
    name,
    email,
    password,
    user_type,
    status: status_backend,
    account_status,
    currency,
    country,
    city,
    address,
    phone,
    ...(report_email ? { report_email } : {}),
  };

  try {
    const cookies = new Cookies();
    const token = cookies.get("token");

    const response = await AxiosInstance.post(API_BASE, body, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
    });

    return response;
  } catch (error) {
    console.error("error in request:", error);
    if(error.response.status == 400 || error.response.status == 404) {
      return error.response;
    }
    return null;
  }
}


export async function BrokersTransactionCreate(payload) {
  if (!payload || typeof payload !== "object") {
    return logValidationError("Debes enviar un objeto 'payload'.");
  }

  const email = String(payload.email ?? "").trim();
  const type = normalizeTxType(payload.type);
  const amountRaw = payload.amount;
  const comment = String(payload.comment ?? "").trim();

  if (!email) return logValidationError("Falta el campo obligatorio: email");
  if (!type) return logValidationError("Falta el campo obligatorio: type");
  if (amountRaw === undefined || amountRaw === null || amountRaw === "") {
    return logValidationError("Falta el campo obligatorio: amount");
  }

  const amount = Number(amountRaw);
  if (!Number.isFinite(amount) || amount <= 0) {
    return logValidationError("amount invalido. Debe ser un numero > 0.");
  }

  const body = {
    email,
    type,
    amount,
    ...(comment ? { comment } : {}),
  };

  try {
    const cookies = new Cookies();
    const token = cookies.get("token");

    const res = await AxiosInstance.post(API_TRANSACTION, body, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
    });

    return res;
  } catch (error) {
    console.error("error in request:", error);
    return null;
  }
}

// --------------------------------------------------------
// EXPORT DEFAULT
// --------------------------------------------------------
const methods = {
  BrokersIBs,
  BrokersIBsUpdateStatus,
  CreateBroker,
  UpdateBroker,
  DeleteBroker,
  BrokersTransactionCreate,
};

export default methods;
